import {useEffect, useState} from "react";
import {
    Box, Button, Dialog, DialogContent, IconButton, Radio, RadioGroup, FormControlLabel, Typography
} from "@mui/material";
import {
    Close as CloseIcon,
    Settings as SettingsIcon
} from "@mui/icons-material";
import {RTL} from "../../cache/CacheRtl";
import {colors, borders} from "../ui/colors";
import {updateTraineeSettings} from "../../services/TraineeService";
import {useMessageContext} from "../../contexts/MessageContext";
import {getErrorMessage} from "../../enums/ErrorMessages";

const INHERIT = "INHERIT";
const SHOW = "SHOW";
const HIDE = "HIDE";

const fromOverride = (override) => {
    if (override === true) return SHOW;
    if (override === false) return HIDE;
    return INHERIT;
};

const toOverride = (value) => {
    if (value === SHOW) return true;
    if (value === HIDE) return false;
    return null;
};

const UserSettingsDialog = ({isOpen, close, client, onSaved}) => {
    const {notificationHandler} = useMessageContext();
    const [value, setValue] = useState(INHERIT);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setValue(fromOverride(client?.showOriginalRepsRangeOverride));
        }
    }, [isOpen, client]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateTraineeSettings(client.id, {
                showOriginalRepsRangeOverride: toOverride(value),
            });
            notificationHandler.success("הגדרות נשמרו בהצלחה");
            onSaved?.();
            close();
        } catch (e) {
            notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={close}
            PaperProps={{
                sx: {
                    borderRadius: borders.radius.lg,
                    minWidth: {xs: "90%", sm: "440px"},
                    maxWidth: "520px",
                    overflowY: "auto"
                }
            }}
        >
            <RTL>
                <Box dir="rtl">
                    {/* Header */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 20px",
                        backgroundColor: colors.bg,
                        borderBottom: borders.default
                    }}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <SettingsIcon sx={{color: colors.textSecondary, fontSize: "20px"}} />
                            <Typography sx={{fontWeight: 600, fontSize: "16px", color: colors.textPrimary}}>
                                הגדרות מתאמן
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={close}
                            size="small"
                            sx={{color: colors.textMuted, "&:hover": {backgroundColor: colors.bgHover, color: colors.textPrimary}}}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <DialogContent sx={{padding: "20px"}}>
                        <Typography sx={{fontSize: "13px", fontWeight: 600, color: colors.textPrimary, marginBottom: "8px"}}>
                            הצגת טווח חזרות מקורי
                        </Typography>
                        <Typography sx={{fontSize: "12px", color: colors.textMuted, marginBottom: "12px"}}>
                            שולט בהצגת טווח החזרות שהוגדר בתוכנית עבור מתאמן זה
                        </Typography>
                        <RadioGroup
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                        >
                            <FormControlLabel
                                value={INHERIT}
                                control={<Radio size="small" sx={{"&.Mui-checked": {color: colors.primary}}}/>}
                                label={<Typography sx={{fontSize: "13px"}}>ברירת מחדל של החברה</Typography>}
                            />
                            <FormControlLabel
                                value={SHOW}
                                control={<Radio size="small" sx={{"&.Mui-checked": {color: colors.primary}}}/>}
                                label={<Typography sx={{fontSize: "13px"}}>הצג טווח חזרות</Typography>}
                            />
                            <FormControlLabel
                                value={HIDE}
                                control={<Radio size="small" sx={{"&.Mui-checked": {color: colors.primary}}}/>}
                                label={<Typography sx={{fontSize: "13px"}}>הסתר טווח חזרות</Typography>}
                            />
                        </RadioGroup>
                    </DialogContent>

                    {/* Footer */}
                    <Box sx={{
                        display: "flex", justifyContent: "flex-start", gap: 1.5,
                        padding: "16px 20px", borderTop: borders.default, backgroundColor: colors.bg
                    }}>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            variant="contained"
                            sx={{
                                backgroundColor: colors.primary, color: colors.white, fontSize: "13px", fontWeight: 600,
                                padding: "8px 24px", borderRadius: borders.radius.md, textTransform: "none",
                                "&:hover": {backgroundColor: colors.primaryDark},
                                "&.Mui-disabled": {backgroundColor: colors.border, color: colors.textMuted}
                            }}
                        >
                            שמור
                        </Button>
                        <Button onClick={close} variant="outlined"
                            sx={{
                                borderColor: colors.border, color: colors.textSecondary, fontSize: "13px", fontWeight: 500,
                                padding: "8px 20px", borderRadius: borders.radius.md, textTransform: "none",
                                "&:hover": {borderColor: colors.textSecondary, backgroundColor: colors.bgHover}
                            }}>
                            ביטול
                        </Button>
                    </Box>
                </Box>
            </RTL>
        </Dialog>
    );
};

export default UserSettingsDialog;
